import tiaLogo from "@assets/logo-tia-full.svg";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import Listing from "@particles/listing/Listing";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import BreakingNews from "@routes/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@routes/Section/components/MostPopular/MostPopular";
import { StoryThreadPayload } from "@routes/StoryThread/utils/types";
import { cn } from "@util/helpers";

type DefaultStoryThreadLayoutProps = Pick<StoryThreadPayload, "data">;

/**
 * Default layout for Story Thread pages.
 */
const DefaultStoryThreadLayout = ({
  data: { articles, seoFooterData, entity },
  gaProps,
}: DefaultStoryThreadLayoutProps & {
  gaProps: GADataProps;
}): React.ReactElement => {
  useInjectClientData({ gaProps });

  const breakingNews = useTrimmedBreakingNews();
  const mostPopular = useMostReadArticleList();

  const {
    tag: { name, urlPath },
  } = entity;
  const relativePath = getUrlPathFromAbsoluteUrl(urlPath);

  return (
    <div className="w-full">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="pt-6">
        <div className="mb-6 border-b border-gray-850 pb-3 pt-6">
          {relativePath === "/tech-in-asia" ? (
            <img
              src={tiaLogo}
              alt="Tech in Asia"
              width={271}
              height={47}
              className="mx-auto lg:mx-0"
            />
          ) : (
            <h1
              className={cn(
                "font-playfair text-6xl font-semibold uppercase text-gray-850",
                "md:text-8xl"
              )}
            >
              {name}
            </h1>
          )}
        </div>

        <div className="grid lg:grid-cols-12">
          <div className="mb-6 lg:col-span-8">
            <Listing
              cardProps={articles}
              pageType={"storyThread"}
              size={10}
              urlPath={urlPath}
              isUseTemplateH
            />
          </div>

          <div className="mb-6 lg:col-span-4 lg:pl-6">
            <div className="mb-6 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-[60px]"
              />
            </div>

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />

            <MostPopular
              data={mostPopular}
              rootClassName="max-w-[300px] mx-auto"
            />
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default DefaultStoryThreadLayout;
